/**
 * Version Management Hook
 *
 * Provides version management functionality for schedule entities.
 * Handles fetching, comparing, and restoring versions.
 */

import { useState, useEffect, useCallback } from 'react'
import { versionService } from '../services/version-service'
import type { Version, VersionDiff, EntityType } from '../types/versioned-entity.types'

export interface UseVersionManagementOptions {
  entityType: EntityType
  entityId: string
  autoFetch?: boolean
}

export interface UseVersionManagementReturn {
  versions: Version[]
  loading: boolean
  error: Error | null
  currentVersion: Version | null
  fetchVersions: () => Promise<void>
  createVersion: (data: Record<string, unknown>, note?: string) => Promise<Version>
  compareVersions: (v1Id: string, v2Id: string) => Promise<VersionDiff>
  restoreVersion: (versionId: string) => Promise<Version>
  archiveVersion: (versionId: string) => Promise<void>
}

export function useVersionManagement({
  entityType,
  entityId,
  autoFetch = true,
}: UseVersionManagementOptions): UseVersionManagementReturn {
  const [versions, setVersions] = useState<Version[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchVersions = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const fetchedVersions = await versionService.getVersions(entityType, entityId)
      setVersions(fetchedVersions)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch versions'))
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId])

  const createVersion = useCallback(
    async (data: Record<string, unknown>, note?: string): Promise<Version> => {
      setLoading(true)
      setError(null)

      try {
        const newVersion = await versionService.createVersion(
          entityType,
          entityId,
          data,
          note
        )
        await fetchVersions() // Refresh list
        return newVersion
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create version')
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [entityType, entityId, fetchVersions]
  )

  const compareVersions = useCallback(
    async (v1Id: string, v2Id: string): Promise<VersionDiff> => {
      setLoading(true)
      setError(null)

      try {
        return await versionService.compareVersions(entityType, entityId, v1Id, v2Id)
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to compare versions')
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [entityType, entityId]
  )

  const restoreVersion = useCallback(
    async (versionId: string): Promise<Version> => {
      setLoading(true)
      setError(null)

      try {
        const restoredVersion = await versionService.restoreVersion(
          entityType,
          entityId,
          versionId
        )
        await fetchVersions() // Refresh list
        return restoredVersion
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to restore version')
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [entityType, entityId, fetchVersions]
  )

  const archiveVersion = useCallback(
    async (versionId: string): Promise<void> => {
      setLoading(true)
      setError(null)

      try {
        await versionService.archiveVersion(entityType, entityId, versionId)
        await fetchVersions() // Refresh list
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to archive version')
        setError(error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [entityType, entityId, fetchVersions]
  )

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchVersions()
    }
  }, [autoFetch, fetchVersions])

  // Get current version (first active version in list)
  const currentVersion = versions.find(v => v.status === 'active') || versions[0] || null

  return {
    versions,
    loading,
    error,
    currentVersion,
    fetchVersions,
    createVersion,
    compareVersions,
    restoreVersion,
    archiveVersion,
  }
}
